import {MouseEventHandler, ReactNode, useState} from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import {SxProps, Theme} from '@mui/material/styles';
import {breakpoint, kit, radius} from './tokens';

export type HomeownerNavKey = 'myProjects' | 'findContractors' | 'messages';
export type PublicNavKey = 'findContractors' | 'forContractors';
export type ContractorNavKey =
  | 'home'
  | 'openJobs'
  | 'myJobs'
  | 'myQuotes'
  | 'messages';

const HOMEOWNER_LINKS: {key: HomeownerNavKey; label: string}[] = [
  {key: 'myProjects', label: 'My Projects'},
  {key: 'findContractors', label: 'Find Contractors'},
  {key: 'messages', label: 'Messages'},
];

// Public bar: the homeowner-facing link sits on the left like every other
// role's links; the contractor recruiting link lives on the right beside
// Sign in, worded as an action so it can't be mistaken for "Find
// Contractors" (the two read as near-identical side by side).
const PUBLIC_LINKS: {key: PublicNavKey; label: string}[] = [
  {key: 'findContractors', label: 'Find Contractors'},
];
const PUBLIC_CONTRACTOR_LINK: {key: PublicNavKey; label: string} = {
  key: 'forContractors',
  label: 'Join as a contractor',
};
const PUBLIC_MENU_LINKS = [...PUBLIC_LINKS, PUBLIC_CONTRACTOR_LINK];

const CONTRACTOR_LINKS: {key: ContractorNavKey; label: string}[] = [
  {key: 'home', label: 'Home'},
  {key: 'openJobs', label: 'Open Jobs'},
  {key: 'myJobs', label: 'My Jobs'},
  {key: 'myQuotes', label: 'My Quotes'},
  {key: 'messages', label: 'Messages'},
];

const LOGO_SRC = 'https://cdn.bidmii.com/media/web_assets/logo.webp';

interface TopNavBaseProps {
  onLogoClick?: MouseEventHandler<HTMLElement>;
  sx?: SxProps<Theme>;
}

interface HomeownerTopNavProps extends TopNavBaseProps {
  role: 'homeowner';
  activeKey?: HomeownerNavKey;
  onNavigate?: (key: HomeownerNavKey) => void;
  // Real notification-bell / avatar-menu nodes -- this kit only reserves
  // their slot; the nav crew wires the real unread state and click handlers.
  bell?: ReactNode;
  avatar?: ReactNode;
}

interface ContractorTopNavProps extends TopNavBaseProps {
  role: 'contractor';
  activeKey?: ContractorNavKey;
  onNavigate?: (key: ContractorNavKey) => void;
  bell?: ReactNode;
  avatar?: ReactNode;
}

interface PublicTopNavProps extends TopNavBaseProps {
  role: 'public';
  activeKey?: PublicNavKey;
  onNavigate?: (key: PublicNavKey) => void;
  onSignIn?: () => void;
  onPostProject?: () => void;
}

export type TopNavProps =
  | HomeownerTopNavProps
  | ContractorTopNavProps
  | PublicTopNavProps;

const NavLink = ({
  label,
  active,
  onClick,
}: {
  label: string;
  active?: boolean;
  onClick?: () => void;
}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      appearance: 'none',
      background: 'none',
      border: 'none',
      padding: 0,
      margin: 0,
      font: 'inherit',
      cursor: 'pointer',
      fontSize: 14.5,
      fontWeight: 600,
      color: active ? kit.ink : kit.slate,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      position: 'relative',
      whiteSpace: 'nowrap',
      '&::after': active
        ? {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '2.5px',
            borderRadius: '999px 999px 0 0',
            backgroundColor: kit.blue,
          }
        : undefined,
    }}>
    {label}
  </Box>
);

// Nav-links row: hidden below the tablet line -- at that width the
// signed-in roles hand navigation off to BottomTabBar, and the public bar
// folds its links into PublicPhoneMenu (there's no bottom-tab destination
// for a signed-out visitor, and a 360px bar has no room for another link
// beside Sign in + Post a project).
const navRowSx = (theme: Theme) => ({
  display: 'flex',
  gap: '30px',
  height: '100%',
  [theme.breakpoints.down(breakpoint.tablet)]: {display: 'none'},
});

// Phone-only menu button for the public bar's links -- the pre-4.0 public
// nav likewise kept its Contractors link in a drawer on phones.
const PublicPhoneMenu = ({
  onNavigate,
}: {
  onNavigate?: (key: PublicNavKey) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <IconButton
        aria-label="Menu"
        size="small"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={(theme) => ({
          display: 'none',
          color: kit.inkSoft,
          [theme.breakpoints.down(breakpoint.tablet)]: {display: 'inline-flex'},
        })}>
        <MenuIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}>
        {PUBLIC_MENU_LINKS.map((link) => (
          <MenuItem
            key={link.key}
            onClick={() => {
              setAnchorEl(null);
              onNavigate?.(link.key);
            }}
            sx={{fontSize: 15, fontWeight: 600, color: kit.ink}}>
            {link.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// The approved one-nav top bar (web-nav-system.html specimens 1-3): logo,
// role-based links with an active underline, and a right-side slot that's
// either bell+avatar (signed in) or Sign in + Post a project (public).
const TopNav = (props: TopNavProps) => {
  const {onLogoClick, sx} = props;

  return (
    <Box
      component="header"
      sx={[
        {backgroundColor: '#FFFFFF', borderBottom: `1px solid ${kit.line}`},
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}>
      <Box
        sx={(theme) => ({
          maxWidth: 1200,
          margin: '0 auto',
          height: 64,
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          gap: '40px',
          [theme.breakpoints.down(breakpoint.tablet)]: {
            height: 56,
            padding: '0 16px',
            gap: 0,
          },
        })}>
        <Box
          component={onLogoClick ? 'button' : 'span'}
          type={onLogoClick ? 'button' : undefined}
          onClick={onLogoClick}
          sx={{
            appearance: 'none',
            background: 'none',
            border: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexShrink: 0,
            cursor: onLogoClick ? 'pointer' : 'default',
          }}>
          <Box
            component="img"
            src={LOGO_SRC}
            alt="Bidmii"
            sx={{height: 26, display: 'block'}}
          />
        </Box>

        {props.role === 'public' ? (
          <Box component="nav" sx={navRowSx}>
            {PUBLIC_LINKS.map((link) => (
              <NavLink
                key={link.key}
                label={link.label}
                active={props.activeKey === link.key}
                onClick={() => props.onNavigate?.(link.key)}
              />
            ))}
          </Box>
        ) : (
          <Box component="nav" sx={navRowSx}>
            {(props.role === 'homeowner'
              ? HOMEOWNER_LINKS
              : CONTRACTOR_LINKS
            ).map((link) => (
              <NavLink
                key={link.key}
                label={link.label}
                active={props.activeKey === link.key}
                onClick={() => {
                  if (props.role === 'homeowner') {
                    props.onNavigate?.(link.key as HomeownerNavKey);
                  } else {
                    props.onNavigate?.(link.key as ContractorNavKey);
                  }
                }}
              />
            ))}
          </Box>
        )}

        <Box
          sx={(theme) => ({
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            // Tighter on phones so the public bar's menu button fits beside
            // Sign in + Post a project at 360px.
            [theme.breakpoints.down(breakpoint.tablet)]: {gap: '8px'},
          })}>
          {props.role === 'public' ? (
            <>
              <Box
                component="button"
                type="button"
                onClick={() => props.onNavigate?.(PUBLIC_CONTRACTOR_LINK.key)}
                sx={(theme) => ({
                  appearance: 'none',
                  background: 'none',
                  border: 'none',
                  padding: '0 4px',
                  margin: '0 10px 0 0',
                  font: 'inherit',
                  cursor: 'pointer',
                  fontSize: 14.5,
                  fontWeight: 600,
                  color:
                    props.activeKey === PUBLIC_CONTRACTOR_LINK.key
                      ? kit.blueInk
                      : kit.slate,
                  whiteSpace: 'nowrap',
                  // Folds into PublicPhoneMenu below the tablet line.
                  [theme.breakpoints.down(breakpoint.tablet)]: {
                    display: 'none',
                  },
                })}>
                {PUBLIC_CONTRACTOR_LINK.label}
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => props.onSignIn?.()}
                sx={{
                  appearance: 'none',
                  background: 'none',
                  border: 'none',
                  padding: '0 4px',
                  margin: 0,
                  font: 'inherit',
                  cursor: 'pointer',
                  fontSize: 14.5,
                  fontWeight: 600,
                  color: kit.inkSoft,
                  whiteSpace: 'nowrap',
                }}>
                Sign in
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => props.onPostProject?.()}
                sx={(theme) => ({
                  appearance: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 42,
                  padding: '0 18px',
                  borderRadius: `${radius.md}px`,
                  backgroundColor: kit.blue,
                  color: '#FFFFFF',
                  fontFamily: 'inherit',
                  fontSize: 14,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  boxShadow: '0 6px 14px rgba(28,167,229,.28)',
                  [theme.breakpoints.down(breakpoint.tablet)]: {
                    padding: '0 14px',
                  },
                })}>
                Post a project
              </Box>
              <PublicPhoneMenu onNavigate={props.onNavigate} />
            </>
          ) : (
            <>
              {props.bell}
              {props.avatar}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TopNav;
